import { useLayoutEffect, useRef, useState, type CSSProperties, type ReactNode } from "react";
import { useMediaQuery } from "../../../hooks/useMediaQuery";
import { reviewHeaderLayout, reviewHeaderProgress } from "./reviewHeaderProgress";

/** Scrub the same layout forwards and backwards with the page's scroll position. */
export function AnimatedReviewHeader({
  expanded,
  compact,
  status,
  tabs,
  actions,
}: {
  expanded: ReactNode;
  compact: ReactNode;
  status: ReactNode;
  tabs: ReactNode;
  actions: ReactNode;
}) {
  const root = useRef<HTMLDivElement>(null);
  const identity = useRef<HTMLDivElement>(null);
  const statusAnchor = useRef<HTMLSpanElement>(null);
  const tabRow = useRef<HTMLDivElement>(null);
  const [progress, setProgress] = useState(0);
  const layout = reviewHeaderLayout(progress, useMediaQuery("lg"));
  // Hand the collapsed height back to the page in the same commit as the
  // height change, before paint, so the scroll range never moves under it.
  useLayoutEffect(() => {
    const page = root.current?.closest<HTMLElement>("[data-regulation-page]");
    if (!page) return;
    page.style.setProperty("--review-header-give-back", `${layout.giveBack}px`);
    return () => {
      page.style.removeProperty("--review-header-give-back");
    };
  }, [layout.giveBack]);
  useLayoutEffect(() => {
    const header = root.current;
    const identityRow = identity.current;
    const anchor = statusAnchor.current;
    const tabsElement = tabRow.current;
    if (!header || !identityRow || !anchor || !tabsElement) return;
    const measure = () => {
      header.style.setProperty(
        "--header-status-start",
        `${anchor.getBoundingClientRect().left - header.getBoundingClientRect().left}px`,
      );
      header.style.setProperty("--header-tabs-width", `${tabsElement.offsetWidth}px`);
    };
    measure();
    const observer = new ResizeObserver(measure);
    observer.observe(identityRow);
    observer.observe(tabsElement);
    for (const child of identityRow.children) observer.observe(child);
    return () => observer.disconnect();
  }, []);
  useLayoutEffect(() => {
    const scroll = root.current
      ?.closest("[data-regulation-page]")
      ?.querySelector(".regulation-page-scroll-fade");
    if (!scroll) return;
    let frame = 0;
    const measure = () => {
      frame = 0;
      setProgress(reviewHeaderProgress(scroll.scrollTop));
    };
    const schedule = () => {
      if (!frame) frame = requestAnimationFrame(measure);
    };
    measure();
    scroll.addEventListener("scroll", schedule, { passive: true });
    return () => {
      scroll.removeEventListener("scroll", schedule);
      cancelAnimationFrame(frame);
    };
  }, []);
  return (
    <div
      ref={root}
      data-header-progress={progress}
      data-header-compact={progress === 1}
      style={
        {
          height: layout.height,
          "--header-progress": progress,
          "--header-expanded-opacity": Math.max(0, 1 - progress / 0.45),
          "--header-status-progress": 1 - (1 - progress) ** 2,
        } as CSSProperties
      }
      className="relative [--header-status-gap:20px]"
    >
      <div
        data-header-divider
        aria-hidden
        className="pointer-events-none absolute bottom-0 h-px bg-border/60"
        style={{
          left: "calc(-1 * var(--review-header-inset) * var(--header-progress))",
          right: "calc(-1 * var(--review-header-inset) * var(--header-progress))",
        }}
      />
      <div
        data-header-title-viewport
        className="absolute inset-x-0 bottom-11 top-[88px] overflow-hidden lg:top-12"
      >
        <div
          data-header-expanded
          aria-hidden={progress >= 0.45}
          inert={progress >= 0.45}
          style={{
            opacity: "var(--header-expanded-opacity)",
          }}
          className="lg:pr-48"
        >
          {expanded}
        </div>
      </div>
      <div
        ref={identity}
        data-header-identity
        className="absolute left-[calc(-0.5*var(--review-header-inset))] right-40 top-2 flex items-center gap-2 sm:right-20 lg:right-72"
      >
        {compact}
        <span ref={statusAnchor} aria-hidden className="shrink-0" />
      </div>
      <div
        ref={tabRow}
        data-header-tabs
        className="absolute bottom-0 left-[calc(8px+24px*var(--header-progress))] w-fit"
      >
        {tabs}
      </div>
      <div
        data-header-status
        className="absolute right-0 top-2 flex min-h-8 min-w-0 items-center justify-end lg:justify-start left-10 lg:left-[var(--header-status-left)]"
        style={{
          "--header-status-left": "calc(var(--header-status-start, 140px) + (32px + var(--header-tabs-width, 207px) + var(--header-status-gap) - var(--header-status-start, 140px)) * var(--header-status-progress))",
        } as CSSProperties}
      >
        {status}
      </div>
      <div className="absolute right-0 top-11 flex items-center gap-2 lg:top-2">{actions}</div>
    </div>
  );
}
